'use client'

import { Box, Loader, SimpleGrid, Skeleton, Text } from '@mantine/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { currentAppEnvConfigs } from '@/configs/env'
import { predictionEventContractsQueryOptions } from '@/services/prediction/contracts'
import { predictionEventKindGroupsQueryOptions } from '@/services/prediction/eventKindGroups'
import { enrichEventsWithContracts, predictionEventsAssetType } from '@/services/prediction/events'
import {
  buildEventKindGroupIconMap,
  filterL1NavRoots,
  predictionNavTreeQueryOptions,
} from '@/services/prediction/navigationTree'
import { predictionNavTreeLiveQueryOptions } from '@/services/prediction/navigationTreeLive'
import {
  type PredictionEvent,
  type PredictionNavNode,
  type PredictionSort,
} from '@/services/prediction/types'

import SortMenu from '../SortMenu'
import CursorPagination from '../StockTable/CursorPagination'
import { FutureCard } from './components/EventCard/FutureCard'
import cardStyles from './components/EventCard/index.module.css'
import { EventCardSkeleton } from './components/EventCard/Skeleton'
import { TwoWayCard } from './components/EventCard/TwoWayCard'
import { isRecurringEvent } from './components/EventCard/utils/recurring'
import NavChipList, { type NavChipListItem } from './components/NavChipList'
import NavTreeSidebar from './components/NavTreeSidebar'
import SecondLevelNavTree from './components/SecondLevelNavTree'
import { PREDICTION_PAGE_SIZE, usePagedPredictionEvents } from './hooks/usePagedPredictionEvents'
import styles from './index.module.css'

/**
 * 可见性判定（注入使用页 hook）：已过期 recurring 事件不进池、不占页。
 * Port of cdc-web `BaseLeagueSection/index.tsx` 的 `filteredData`——stag BE
 * 的 `status=active` 对 duration>0 的滚动事件过滤不严,过期窗口（如数天前
 * 的 BTC/ETH 期）仍会返回。到点消失靠数据层轮询（active 事件 5s）兜底,
 * 不做 cdc-web 的精确 expiryTick。无 expired_at 的 recurring 事件不误杀。
 */
const isNotExpiredRecurringEvent = (event: PredictionEvent) =>
  !isRecurringEvent(event) || !event.expired_at || new Date(event.expired_at).getTime() > Date.now()

/** L1 顶栏 chips 的记忆 key（NavChipList 跨重挂载的「只首滚一次 + 停留位置」）。 */
const CHIP_TRACK_KEY = 'homepagev2-prediction-l1'

/** 首轮骨架卡数量（两列网格 ×5 行，对齐一页 10 张）。 */
const SKELETON_COUNT = 10

/**
 * Prediction sort 选项：复用 Stock/Crypto 的 SortMenu 控件（WWW-5151 样式
 * 对齐）。值即 BE `sort_by`（PredictionSort，区别于 cdc-web 的 FE slug +
 * EVENT_SORT_API_VALUE 映射层）；labelKey 用完整 i18n key（SortMenu 全局
 * t()，与 CryptoMarket 传法一致）。
 */
const SORT_OPTIONS: readonly { value: PredictionSort; labelKey: string }[] = [
  { value: 'cumulative_volume', labelKey: 'HomePageV2.Prediction.sortCumulativeVolume' },
  { value: 'rolling_24h_volume', labelKey: 'HomePageV2.Prediction.sortRolling24hVolume' },
  { value: 'event_date', labelKey: 'HomePageV2.Prediction.sortEventDate' },
  { value: 'published_at', labelKey: 'HomePageV2.Prediction.sortPublishedAt' },
]

const PREDICTION_SORT_OPTIONS: readonly PredictionSort[] = SORT_OPTIONS.map(option => option.value)

interface Selection {
  /** 当前选中的 L1 根节点（决定左树与「有效节点」兜底）。 */
  root: PredictionNavNode
  /** 选中的 L2 节点；null = L1 根自身（folder 根取 synthesised 全集过滤）。 */
  child: PredictionNavNode | null
}

interface PredictionSectionProps {
  /** URL `l1` 参数：初始展示的 L1 分类 slug。缺失/不识别时回落 Trending。 */
  l1?: string
}

/**
 * Prediction tab（HomepageV2）。布局：L1 chips + sort 整行置顶（不受左树
 * 影响），其下桌面左树（263px）+ 两列事件网格；tablet 起隐藏左树、移动端
 * 单列。
 *
 * 选择状态纯本地（无 URL 同步，决策 7b）：L1 chip 切换 → 重置 L2 到根；L2
 * 选择 → 保留所属 L1。BE filter 的有效节点解析与 cdc-web 一致：
 * destination → 自身；folder → synthesiseFolderFilter 的全集过滤。
 *
 * URL `l1` 参数只读地决定**初始**选中项，用户点击后不再回写 URL（刷新/分享
 * 才重新生效）——与 asset_nav 的单向读取语义一致。
 */
export default function PredictionSection({ l1 }: Readonly<PredictionSectionProps>) {
  const t = useTranslations('HomePageV2.Prediction')
  const [selection, setSelection] = useState<Selection | null>(null)
  // 用户显式选择的 sort；null = 跟随当前节点的分层默认（cdc-web `?sort=`
  // 缺省语义：切 category 后回落到新页默认，不跨页保留）。
  const [sortOverride, setSortOverride] = useState<PredictionSort | null>(null)

  const { data: roots, isPending: navPending } = useQuery(predictionNavTreeQueryOptions())

  // L1 chips 只渲染非 scope、非 placeholder sentinel、非 0 事件的根
  // （git 移植自 cdc-web filterL1NavRoots，见 services/prediction/navigationTree）。
  const l1Nodes = useMemo(() => filterL1NavRoots(roots), [roots])

  // 未显式选择时的初始选中项（不写回 state，避免 nav tree 轮询刷新引用变化
  // 时无谓重渲染）。三级 fallback：
  //   1. URL `l1` 参数命中的 L1 slug（`l1Nodes` 里查,顺带挡掉被 filterL1NavRoots
  //      排除的节点——pro-football-hub 这类 placeholder 即使写进 URL 也选不中）；
  //   2. Trending —— 用 filter.trending 判定而非裸 slug,与下方 isTrendingRoot
  //      同一套语义,且不依赖 BE 下发的数组顺序（Trending 并非恒为第一个）；
  //   3. 列表第一个（BE 没下发 Trending 时的兜底,即本参数引入前的原有行为）。
  const initialRoot = useMemo(() => {
    const fromParam = l1 ? l1Nodes.find(node => node.slug === l1) : undefined
    return fromParam ?? l1Nodes.find(node => node.filter?.trending === 'true') ?? l1Nodes[0]
  }, [l1, l1Nodes])

  const rootNode = selection?.root ?? initialRoot
  const childNode = selection && selection.root.id === rootNode?.id ? selection.child : null

  // 有效节点：决定事件查询的 BE filter（navNode?.filter ?? null）。
  const effectiveNode = useMemo(() => {
    if (!rootNode) return null
    return childNode ?? rootNode
  }, [rootNode, childNode])

  // 分层默认（cdc-web resolveDefaultSort / OG-10294 AC3）：L1 landing →
  // cumulative_volume；L2 subcategory → event_date。用户主动选过后用 override。
  const isLanding = childNode == null
  const defaultSort: PredictionSort = isLanding ? 'cumulative_volume' : 'event_date'
  // WWW-5088：Live / Trending 根（sentinel，filter live/trending='true'）固定
  // 用第一个 sort 值（PREDICTION_SORT_OPTIONS[0] = cumulative_volume）并隐藏
  // sort 控件；其余 tab 保持「分层默认 + 用户 override + 控件展示」不变。
  const isLiveRoot = rootNode?.filter?.live === 'true'
  const isTrendingRoot = rootNode?.filter?.trending === 'true'
  const hideSort = isLiveRoot || isTrendingRoot
  const sortBy = hideSort ? PREDICTION_SORT_OPTIONS[0] : (sortOverride ?? defaultSort)

  // 页码式翻页（RQ infiniteQuery 骨架 + 当前页窗口）：内存只保留已加载页，
  // 渲染只挂当前页 8 张卡（原来 loadMore 是全量 append、全量渲染）。
  const pagedEvents = usePagedPredictionEvents(
    { navNode: effectiveNode, sortBy },
    { isVisible: isNotExpiredRecurringEvent },
  )

  // ——— 展示窗口状态机 ———
  // 切页瞬间若直接把渲染内容换成新页：事件换批 → contracts 二次查询 key 全换
  // → 新 key 无数据 → merge 丢合约 → 卡片（胜率条/footer）整体塌高 → 页面高度
  // 骤缩骤张 + 视图大幅跳动（之前「列表消失一下、页面滚动」的根因；WWW-5095
  // 蒙层只能遮丑，压不掉滚动）。所以展示窗口与目标页解耦：currentPage 变了
  // 先不动画面，等新页合约就绪再原子推进。
  const [displayPage, setDisplayPage] = useState(1)
  const events = pagedEvents.recordsAt(displayPage)

  // 合约 enrichment（cdc-web CategoryPage 同款）：列表端点不下发合约，卡片
  // 价格/头像全靠 `/og/public/api/v1/contracts` 批量二次查询 merge 回来。
  // key 跟「目标页」而非展示页：推进前展示的旧页由 placeholder（RQ
  // keepPreviousData——上一批合约恰好匹配仍在展示的旧页 id）撑住，画面高度
  // 不变；新 key 合约 fetch 完成、data 替换后（isPlaceholderData 翻 false）
  // 展示窗口才跟进（见 targetReady）。
  const targetIds = useMemo(
    () => pagedEvents.recordsAt(pagedEvents.currentPage).map(e => e.id),
    [pagedEvents, pagedEvents.currentPage],
  )
  const contractsQuery = useQuery({
    ...predictionEventContractsQueryOptions(targetIds),
    enabled: targetIds.length > 0,
    placeholderData: keepPreviousData,
  })
  const { data: contractGroups } = contractsQuery
  const enrichedEvents = useMemo(
    () => enrichEventsWithContracts(events, contractGroups),
    [events, contractGroups],
  )

  // 目标页合约就绪 → 展示窗口推进。判据不含 isPlaceholderData 的旧值：
  // placeholder 期间 data 是「上一批」合约，此时推进仍会塌高，必须等
  // isPlaceholderData=false 的新结果（或请求失败、目标页无卡兜底放行）。
  const targetReady =
    !contractsQuery.isPlaceholderData &&
    (!!contractGroups || contractsQuery.isError || targetIds.length === 0)
  useEffect(() => {
    if (displayPage === pagedEvents.currentPage) return
    if (!targetReady) return
    setDisplayPage(pagedEvents.currentPage)
  }, [displayPage, pagedEvents.currentPage, targetReady])

  // 卡片网格是文档流（不像 Stock 表格定高视口），分页只是池内切片原位替换
  // 内容、物理位置不变。网格仍在视口内（部分或全部可见）→ 保持滚动位置，
  // 新一页的卡直接落在眼前，零跳动；只有网格已滚出视口上方（停在分页器
  // 附近翻页）才把它拉回——正好露出新页开头两行（skip 首挂载）。
  // 观测目标是 displayPage（画面真正换内容的时刻），不是 currentPage。
  const gridSlotRef = useRef<HTMLDivElement | null>(null)
  const prevPageRef = useRef(displayPage)
  useEffect(() => {
    if (prevPageRef.current === displayPage) return
    prevPageRef.current = displayPage
    const slot = gridSlotRef.current
    if (slot && slot.getBoundingClientRect().top < 0) {
      slot.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [displayPage])

  // 页头图标兜底链（cdc-web `useEventKindGroupIcon` 同链）：
  // navigation-tree icon → 组图标 → event.logo_url。
  // - nav-tree icon 优先（buildEventKindGroupIconMap 按 event_kind_group_id
  //   从导航树 icon_url 解析，带 folder 继承）——stag BE 的 logo_url 是
  //   不透明全底徽章（categories/*-bg.png），boxed 40px 头渲染成深色贴片
  //   而非线条图标（CDCW-8699），故 logo_url 只能作为最终兜底；
  // - 组图标不在事件 payload 上，按 event_kind_group_id 从组列表查（组列表
  //   按当前 lane 的 asset_type 拉取）。
  const assetType = predictionEventsAssetType(effectiveNode?.filter ?? null)
  const { data: kindGroups } = useQuery(predictionEventKindGroupsQueryOptions(assetType))
  const kindGroupIconById = useMemo(() => {
    const map = new Map<string, string>()
    for (const group of kindGroups ?? []) {
      if (group.icon) map.set(group.id, group.icon)
    }
    return map
  }, [kindGroups])
  const navIconMap = useMemo(() => buildEventKindGroupIconMap(roots), [roots])
  // icon 解析链（cdc-web `useEventKindGroupIcon` 同款，含 CDCW-8699 后续修复）：
  // nav-tree 的 EKG→icon map 只对 sports 可靠。predicts 类（crypto /
  // financials / politics / culture…）在导航树里是 per-token / per-topic 的
  // 兄弟文件夹，全部重复 category 级 filter 但各自图标不同，
  // first-writer-wins 把整类 collapse 到第一个文件夹（crypto → tokens/BTC，
  // ETH/AVAX 全渲染 BTC badge；politics → electgovernor.png 同理）。所以：
  //   - sports：唯一树节点 → nav-tree icon ?? 组图标 ?? logo_url（stag
  //     logo_url 是不透明徽章，boxed 头需要线条图）；
  //   - predicts：跳过导航树，走 og-web 同款 `logo_url ?? 组图标`（BE 为这
  //     类事件 curated per-card logo：crypto recurring → crypto/logos/<asset>.svg，
  //     长期赛事 → category crypto.png）。
  const resolveIconSrc = (event: (typeof enrichedEvents)[number]) => {
    const groupId = event.event_kind_group_id
    const groupIcon = groupId ? kindGroupIconById.get(groupId) : undefined
    if (event.event_kind_asset_type === 'sports') {
      return (groupId ? navIconMap.get(groupId) : undefined) ?? groupIcon ?? event.logo_url
    }
    return event.logo_url ?? groupIcon
  }

  // Live 根（通用树里是 sentinel 叶子、无 children）：左侧栏的数据源换成
  // curated live 树（navigation-tree/live，flat 子分类），并在最前合成
  // All 行（filter 回归全量 live）。live 树的子分类 filter 带
  // event_kind_groups，事件查询 = live=true + event_kind_groups 过滤。
  const { data: liveTreeNodes } = useQuery({
    ...predictionNavTreeLiveQueryOptions(),
    enabled: isLiveRoot,
  })
  const treeRootNode = useMemo<PredictionNavNode | null>(() => {
    if (!rootNode) return null
    if (!isLiveRoot) return rootNode
    // 合成 All 的 count = live 子树 event_count 之和（cdc-web
    // sumNavTreeLiveEventCount 同语义：BE 快照，与各子分类行同源）。live 根
    // 在通用树里是 sentinel（event_count: null），只能由这里补——漏掉它 All
    // 行就是唯一没数值的那行（WWW-5079）。树未就绪（首载中）给 undefined，
    // RowCount 自然不渲染，避免加载瞬间闪 "(0)"。
    const allEventCount =
      liveTreeNodes === undefined
        ? undefined
        : liveTreeNodes.reduce((total, node) => total + (node.event_count ?? 0), 0)
    const allChild: PredictionNavNode = {
      id: `${rootNode.path}::all`,
      slug: 'all',
      path: `${rootNode.path}/all`,
      display_name: 'All',
      node_type: 'destination',
      ordering: 0,
      icon_url: '',
      filter: { live: 'true' },
      children: [],
      event_count: allEventCount,
    }
    return { ...rootNode, children: [allChild, ...(liveTreeNodes ?? [])] }
  }, [rootNode, isLiveRoot, liveTreeNodes])

  // L2 为空（当前根没有任何非 scope 子节点）时整个左栏隐藏、不占空间。
  const hasL2Children = (treeRootNode?.children ?? []).some(c => c.node_type !== 'scope')

  // 页头 kind 名兜底链：event.event_kind_display_name 常常下空串（实测 stag
  // sports 全空），此时按 event.event_kind 在组列表的 event_kinds 反查组
  // display_name（如 CFB → "College Football"）——stag 卡片头即此值。
  const kindNameByKind = useMemo(() => {
    const map = new Map<string, string>()
    for (const group of kindGroups ?? []) {
      for (const kind of group.event_kinds ?? []) {
        if (!map.has(kind) && group.display_name) map.set(kind, group.display_name)
      }
    }
    return map
  }, [kindGroups])
  const resolveKindName = (event: (typeof enrichedEvents)[number]) =>
    event.event_kind_display_name || kindNameByKind.get(event.event_kind) || undefined

  const cdcWebEventUrl = (event: PredictionEvent) =>
    `https://${currentAppEnvConfigs.cdcWebDomain}/explore/predict/markets/${event.slug}`

  const openEventDetail = (event: (typeof enrichedEvents)[number]) => {
    window.location.assign(cdcWebEventUrl(event))
  }

  const chipItems = useMemo<NavChipListItem[]>(
    () =>
      l1Nodes.map(node => {
        const isLiveChip = node.filter?.live === 'true'
        return {
          slug: node.slug,
          label: node.display_name,
          isActive: node.id === rootNode?.id,
          onClick: () => {
            // 切 L1：回落到新根的分层默认 sort（不跨 category 保留用户 override）。
            setSortOverride(null)
            setSelection({ root: node, child: null })
          },
          iconUrl: node.icon_url || undefined,
          // Live chip：红色广播图标常显（设计稿 8432:136443），不做 hasLive 门控。
          showLiveDot: isLiveChip,
        }
      }),
    [l1Nodes, rootNode?.id],
  )

  const handleTreeSelect = (node: PredictionNavNode) => {
    if (!rootNode) return
    // 切 L2：同样回落到该节点的分层默认 sort（L2 → event_date）。
    setSortOverride(null)
    setSelection({ root: rootNode, child: node.id === rootNode.id ? null : node })
  }

  if (navPending) {
    return (
      <Box className={styles.root} data-section="prediction" aria-busy="true">
        <Text component="h2" variant="heading1" fz={28} lh="40px" c="#F7F9FA" m={0} mb={16}>
          {t('title')}
        </Text>
        <Skeleton height={36} radius={8} />
        <Box className={styles.layout}>
          <Box className={styles.sidebarSlot}>
            <Skeleton height={320} radius={8} />
          </Box>
          <Box className={styles.content}>
            <SimpleGrid cols={{ base: 1, medium: 2 }} spacing="md">
              {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <EventCardSkeleton key={i} />
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={styles.root} data-section="prediction">
      {/* Section 标题与 Stock/Crypto 对齐（heading1 28/40）。 */}
      <Text component="h2" variant="heading1" fz={28} lh="40px" c="#F7F9FA" m={0} mb={16}>
        {t('title')}
      </Text>
      {/* L1 chips 横贯整行、不受左栏影响（设计稿 8432:136438）；溢出用
          scrollArrows 左右箭头滚动（cdc-web 同款交互），不出 "More" 下拉。 */}
      <Box className={styles.controlsRow}>
        <NavChipList items={chipItems} scrollTrackKey={CHIP_TRACK_KEY} scrollArrows />
        {/* Live / Trending（WWW-5088）：不展示 sort 控件，固定第一个 sort 值。 */}
        {!hideSort && (
          <Box className={styles.controlsSort}>
            <SortMenu
              options={SORT_OPTIONS}
              activeValue={sortBy}
              onChange={v => setSortOverride(v as PredictionSort)}
              ariaLabel={t('sortAria')}
            />
          </Box>
        )}
        {/* 移动端 L2 分类条（<midScale 时作为第二行随 controlsRow 一起吸顶，
            桌面隐藏）。folder 型 L1（sports/crypto…）才有子分类；Live /
            Trending 等 destination 根在 SecondLevelNavTree 内部判 null。
            用原始 rootNode 而非 treeRootNode：Live 根合成了 live 子树，不做
            L2 条（cdc-web 同语义）。selectedPath 与桌面左树同源
            （childNode?.path ?? root.path），高亮共享同一套判定。 */}
        {rootNode && (
          <SecondLevelNavTree
            rootNode={rootNode}
            selectedPath={childNode?.path ?? rootNode.path}
            onSelect={handleTreeSelect}
          />
        )}
      </Box>

      <Box className={styles.layout}>
        {treeRootNode && hasL2Children && (
          <Box className={styles.sidebarSlot}>
            <NavTreeSidebar
              rootNode={treeRootNode}
              selectedPath={childNode?.path ?? treeRootNode.path}
              onSelect={handleTreeSelect}
            />
          </Box>
        )}

        <Box ref={gridSlotRef} className={styles.content}>
          {pagedEvents.isPending ? (
            <Box className={styles.grid}>
              {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <EventCardSkeleton key={i} />
              ))}
            </Box>
          ) : enrichedEvents.length === 0 ? (
            <Text variant="subtitle" c="var(--content-secondary)" className={styles.empty}>
              {t('empty')}
            </Text>
          ) : (
            <>
              <Box className={styles.grid}>
                {enrichedEvents.map(event => (
                  <Box key={event.id} className={cardStyles.cardRoot}>
                    {/* 整卡跳转外链到 cdc-web 事件详情（host 按环境配置：
                        staging=cwsta-cdc-web.3ona.co / prod=web.crypto.com，
                        与 getCdcWebSignupLink 同款约定）。绝对定位铺满卡片，
                        当前标签页直接跳转。内部价格格按钮有更高 z-index
                        仍可点（本区价格格无跳转，点击暂为 no-op）。 */}
                    <a
                      href={cdcWebEventUrl(event)}
                      aria-label={event.title}
                      className={cardStyles.cardLinkOverlay}
                    />
                    {/* dispatcher（cdc-web resolveCardVariant3Way 简化版）：
                        league → FutureCard；其余（无 odds 切换时所有 match 卡）
                        → TwoWayCard。 */}
                    {event.event_type === 'league' ? (
                      <FutureCard
                        event={event}
                        iconSrc={resolveIconSrc(event)}
                        kindName={resolveKindName(event)}
                        onContractClick={() => openEventDetail(event)}
                      />
                    ) : (
                      <TwoWayCard
                        event={event}
                        iconSrc={resolveIconSrc(event)}
                        kindName={resolveKindName(event)}
                        onContractClick={() => openEventDetail(event)}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              {/* 页码式翻页器（与 Stock 同款）；未知总数时尾页由 hasMore=false
                  自动收拢。计数文案语义在卡片网格里是「第 N 组」，与 Stock 表格
                  一致显示区间。 */}
              <CursorPagination
                currentPage={pagedEvents.currentPage}
                loadedPageCount={pagedEvents.loadedPageCount}
                hasMore={pagedEvents.hasMore}
                isFetching={pagedEvents.isFetching}
                recordsPerPage={PREDICTION_PAGE_SIZE}
                onChange={pagedEvents.gotoPage}
              />
            </>
          )}
          {/* 翻页蒙层：三个来源——
              1) isNavigating：目标页未入内存、事件 fetch 链进行中（hook 引用
                 计数保证连点到最后才撤）；
              2) displayPage !== currentPage：展示窗口在等新页合约就绪（见
                 上面的 targetReady/推进 effect）——旧页内容此刻仍正确，但
                 给翻页一个明确的「进行中」反馈；
              3) contracts 二次查询在飞且无占位数据：首载/切 category 时
                 merge 前卡片无合约渲染会短暂塌高，蒙层盖住（WWW-5095）。
                 !data 门控：placeholder（keepPreviousData）或 stale 刷新
                 不误盖；查询失败时 data 恒空且 isFetching=false 自然撤掉。
                 半透明 blur 盖住卡片区；spinner fixed 钉在视口中部。 */}
          {(pagedEvents.isNavigating ||
            displayPage !== pagedEvents.currentPage ||
            (contractsQuery.isFetching && !contractsQuery.data)) && (
            <>
              <Box className={styles.fetchOverlay} />
              <Loader size="xl" className={styles.fetchSpinner} role="status" aria-live="polite" />
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
